import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import './ProfilePage.css';

function ProfilePage({ user, onLogout, onUpdateUser }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [logoutConfirm, setLogoutConfirm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notification, setNotification] = useState(null);
  
  // Form states
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    birthMonth: user?.birthMonth || '',
    birthDay: user?.birthDay || '',
    birthYear: user?.birthYear || '',
    gender: user?.gender || '',
    bio: user?.bio || ''
  });


  const [passwordData, setPasswordData] = useState({
    current: '',
    new: '',
    confirm: ''
  });

  const [deletePassword, setDeletePassword] = useState('');

  // Sync profile data when user changes
  useEffect(() => {
    if (user) {
      setProfileData({
        name: user?.name || '',
        email: user?.email || '',
        birthMonth: user?.birthMonth || '',
        birthDay: user?.birthDay || '',
        birthYear: user?.birthYear || '',
        gender: user?.gender || '',
        bio: user?.bio || ''
      });
    }
  }, [user]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleLogoutClick = () => {
    setLogoutConfirm(true);
  };

  const handleLogoutConfirm = () => {
    onLogout();
    navigate('/');
  };

  const handleDeleteAccount = () => {
    setDeleteConfirm(true);
  };

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      setError('');
      
      const dataToSend = {
        ...profileData,
        birthDay: profileData.birthDay ? parseInt(profileData.birthDay, 10) : undefined,
        birthYear: profileData.birthYear ? parseInt(profileData.birthYear, 10) : undefined,
      };
      
      Object.keys(dataToSend).forEach(key => {
        if (dataToSend[key] === undefined || dataToSend[key] === '') {
          delete dataToSend[key];
        }
      });
      
      const updatedUserData = await api.user.updateProfile(dataToSend);
      const updatedUser = updatedUserData.user || updatedUserData;
      
      if (onUpdateUser) {
        await onUpdateUser(updatedUser);
      }
      
      showNotification('Profile updated successfully!');
    } catch (err) {
      console.error('Save profile error:', err);
      showNotification(err.message || 'Failed to update profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (passwordData.new !== passwordData.confirm) {
        showNotification('New passwords do not match!', 'error');
        setLoading(false);
        return;
      }

      if (passwordData.new.length < 8) {
        showNotification('Password must be at least 8 characters long', 'error');
        setLoading(false);
        return;
      }

      await api.user.changePassword(
        passwordData.current,
        passwordData.new,
        passwordData.confirm
      );
      
      showNotification('Password changed successfully!');
      setPasswordData({ current: '', new: '', confirm: '' });
    } catch (err) {
      showNotification(err.message || 'Failed to change password', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccountConfirm = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (!deletePassword) {
        setError('Please enter your password to delete your account');
        setLoading(false);
        return;
      }

      await api.user.deleteAccount(deletePassword);
      
      onLogout();
      navigate('/');
    } catch (err) {
      setError(err.message || 'Failed to delete account');
      setLoading(false);
    }
  };


  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);

  return (
    <div className="profile-page">
      <div className="profile-page__container">
        <div className="profile-page__header">
          <button 
            className="profile-page__back"
            onClick={() => navigate('/')}
            aria-label="Back to home"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </button>
          <h2 className="profile-page__title">Profile & Settings</h2>
          <div style={{ width: '44px' }}></div>
        </div>

        {/* Tabs */}
        <div className="profile-page__tabs">
          <button 
            className={`profile-page__tab ${activeTab === 'profile' ? 'profile-page__tab--active' : ''}`}
            onClick={() => {
              setActiveTab('profile');
              setError('');
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            Profile
          </button>
          <button 
            className={`profile-page__tab ${activeTab === 'security' ? 'profile-page__tab--active' : ''}`}
            onClick={() => {
              setActiveTab('security');
              setError('');
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            Security
          </button>
          <button 
            className={`profile-page__tab ${activeTab === 'account' ? 'profile-page__tab--active' : ''}`}
            onClick={() => {
              setActiveTab('account');
              setError('');
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
            Account
          </button>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="profile-page__body">
            <div className="profile-page__form">
              <div className="profile-page__field">
                <label className="profile-page__label">Full Name</label>
                <input 
                  type="text"
                  className="profile-page__input"
                  value={profileData.name}
                  onChange={(e) => handleProfileChange('name', e.target.value)}
                  placeholder="Enter your name"
                />
              </div>

              <div className="profile-page__field">
                <label className="profile-page__label">Email Address</label>
                <input 
                  type="email"
                  className="profile-page__input"
                  value={profileData.email}
                  onChange={(e) => handleProfileChange('email', e.target.value)}
                  placeholder="your@email.com"
                />
              </div>

              <div className="profile-page__field">
                <label className="profile-page__label">Birthday</label>
                <div className="profile-page__birthday">
                  <select 
                    className="profile-page__input profile-page__birthday-select"
                    value={profileData.birthMonth}
                    onChange={(e) => handleProfileChange('birthMonth', e.target.value)}
                  >
                    <option value="">Month</option>
                    {months.map((month) => (
                      <option key={month} value={month}>{month}</option>
                    ))}
                  </select>
                  <select 
                    className="profile-page__input profile-page__birthday-select"
                    value={profileData.birthDay}
                    onChange={(e) => handleProfileChange('birthDay', e.target.value)}
                  >
                    <option value="">Day</option>
                    {days.map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                  <select 
                    className="profile-page__input profile-page__birthday-select"
                    value={profileData.birthYear}
                    onChange={(e) => handleProfileChange('birthYear', e.target.value)}
                  >
                    <option value="">Year</option>
                    {years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="profile-page__field">
                <label className="profile-page__label">Gender</label>
                <select 
                  className="profile-page__input"
                  value={profileData.gender}
                  onChange={(e) => handleProfileChange('gender', e.target.value)}
                >
                  <option value="">Prefer not to say</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="non-binary">Non-binary</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="profile-page__field">
                <label className="profile-page__label">Bio (Optional)</label>
                <textarea 
                  className="profile-page__textarea"
                  rows="3"
                  value={profileData.bio}
                  onChange={(e) => handleProfileChange('bio', e.target.value)}
                  placeholder="Tell us a bit about yourself..."
                  maxLength="200"
                />
                <span className="profile-page__char-count">{profileData.bio.length}/200</span>
              </div>

              <button className="profile-page__save-btn" onClick={handleSaveProfile} disabled={loading}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                  <polyline points="17 21 17 13 7 13 7 21"/>
                  <polyline points="7 3 7 8 15 8"/>
                </svg>
                Save Changes
              </button>
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="profile-page__body">
            <div className="profile-page__section-title">Change Password</div>
            <p className="profile-page__section-desc">
              Create a strong password to keep your account secure
            </p>

            <div className="profile-page__form">
              <div className="profile-page__field">
                <label className="profile-page__label">Current Password</label>
                <input 
                  type="password"
                  className="profile-page__input"
                  value={passwordData.current}
                  onChange={(e) => handlePasswordChange('current', e.target.value)}
                  placeholder="Enter current password"
                />
              </div>

              <div className="profile-page__field">
                <label className="profile-page__label">New Password</label>
                <input 
                  type="password"
                  className="profile-page__input"
                  value={passwordData.new}
                  onChange={(e) => handlePasswordChange('new', e.target.value)}
                  placeholder="Enter new password"
                />
              </div>

              <div className="profile-page__field">
                <label className="profile-page__label">Confirm New Password</label>
                <input 
                  type="password"
                  className="profile-page__input"
                  value={passwordData.confirm}
                  onChange={(e) => handlePasswordChange('confirm', e.target.value)}
                  placeholder="Confirm new password"
                />
              </div>

              <button className="profile-page__save-btn" onClick={handleChangePassword} disabled={loading}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                Update Password
              </button>
            </div>

            <div className="profile-page__info-box">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="16" x2="12" y2="12"/>
                <line x1="12" y1="8" x2="12.01" y2="8"/>
              </svg>
              <div>
                <strong>Password Tips:</strong>
                <ul>
                  <li>Use at least 8 characters</li>
                  <li>Include numbers and special characters</li>
                  <li>Avoid common words or patterns</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Account Tab */}
        {activeTab === 'account' && (
          <div className="profile-page__body">
            <div className="profile-page__section-title">Account Management</div>
            <p className="profile-page__section-desc">
              Manage your account settings and data
            </p>

            <div className="profile-page__stat-card">
              <div className="profile-page__stat-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </div>
              <div className="profile-page__stat-content">
                <div className="profile-page__stat-label">Member Since</div>
                <div className="profile-page__stat-value">
                  {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </div>
              </div>
            </div>

            <div className="profile-page__actions">
              <button className="profile-page__action-btn" onClick={handleLogoutClick}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/>
                </svg>
                <div className="profile-page__action-text">
                  <div className="profile-page__action-title">Log Out</div>
                  <div className="profile-page__action-desc">Sign out of your account</div>
                </div>
                <svg className="profile-page__action-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>

              <button className="profile-page__action-btn profile-page__action-btn--danger" onClick={handleDeleteAccount}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                </svg>
                <div className="profile-page__action-text">
                  <div className="profile-page__action-title">Delete Account</div>
                  <div className="profile-page__action-desc">Permanently delete your account and all data</div>
                </div>
                <svg className="profile-page__action-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"/>
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Logout Confirmation */}
        {logoutConfirm && (
          <>
            <div className="profile-page__confirm-overlay" onClick={() => setLogoutConfirm(false)} />
            <div className="profile-page__confirm-popup">
              <div className="profile-page__confirm-icon profile-page__confirm-icon--warning">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"/>
                </svg>
              </div>
              <h3 className="profile-page__confirm-title">Log Out?</h3>
              <p className="profile-page__confirm-message">
                Are you sure you want to log out of your account?
              </p>
              <div className="profile-page__confirm-actions">
                <button 
                  className="profile-page__confirm-btn profile-page__confirm-btn--cancel"
                  onClick={() => setLogoutConfirm(false)}
                >
                  Cancel
                </button>
                <button 
                  className="profile-page__confirm-btn profile-page__confirm-btn--confirm"
                  onClick={handleLogoutConfirm}
                >
                  Log Out
                </button>
              </div>
            </div>
          </>
        )}

        {/* Delete Account Confirmation */}
        {deleteConfirm && (
          <>
            <div className="profile-page__confirm-overlay" onClick={() => setDeleteConfirm(false)} />
            <div className="profile-page__confirm-popup">
              <div className="profile-page__confirm-icon profile-page__confirm-icon--danger">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                  <line x1="12" y1="9" x2="12" y2="13"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
              </div>
              <h3 className="profile-page__confirm-title">Delete Account?</h3>
              <p className="profile-page__confirm-message">
                This action cannot be undone. All your entries and data will be permanently deleted.
              </p>
              
              {error && (
                <div className="profile-page__error-inline">
                  {error}
                </div>
              )}
              
              <div className="profile-page__field">
                <label className="profile-page__label" htmlFor="delete-password">
                  Enter your password to confirm
                </label>
                <input
                  id="delete-password"
                  type="password"
                  className="profile-page__input"
                  placeholder="Your password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  autoComplete="current-password"
                />
              </div>
              
              <div className="profile-page__confirm-actions">
                <button 
                  className="profile-page__confirm-btn profile-page__confirm-btn--cancel"
                  onClick={() => {
                    setDeleteConfirm(false);
                    setDeletePassword('');
                    setError('');
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button 
                  className="profile-page__confirm-btn profile-page__confirm-btn--danger"
                  onClick={handleDeleteAccountConfirm}
                  disabled={loading || !deletePassword}
                >
                  {loading ? 'Deleting...' : 'Delete Account'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Notification Overlay */}
      {notification && (
        <div className="profile-page__notification-overlay">
          <div className={`profile-page__notification profile-page__notification--${notification.type}`}>
            <div className="profile-page__notification-icon">
              {notification.type === 'success' ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              )}
            </div>
            <p className="profile-page__notification-message">{notification.message}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfilePage;
